import React, { useState } from 'react';
import { feedbackService } from '../services/feedbackService';
import { useEditMode } from '../context/EditModeContext';
import { useAuth } from '../auth/AuthContext';
import { useCalendario } from '../contexts/CalendarioContext';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const TIPOS = [
    { value: 'Problema', label: 'Problema', icon: <BugReportIcon fontSize="small" />, color: '#dc3545' },
    { value: 'Sugerencia', label: 'Sugerencia', icon: <LightbulbIcon fontSize="small" />, color: '#fd7e14' },
    { value: 'Pregunta', label: 'Pregunta', icon: <HelpOutlineIcon fontSize="small" />, color: '#0d6efd' },
];

/**
 * Botón flotante de feedback visible en VistaDetalladaTramiteART.
 * Props:
 *   tramite — objeto tramite actual (para capturar estado automáticamente)
 */
function FeedbackButton({ tramite }) {
    const { isEditMode, customizations } = useEditMode();
    const { user } = useAuth();
    const { calendarioAbierto } = useCalendario();
    const location = useLocation();

    const [open, setOpen] = useState(false);
    const [tipo, setTipo] = useState('Problema');
    const [descripcion, setDescripcion] = useState('');
    const [foto, setFoto] = useState(null);
    const [enviando, setEnviando] = useState(false);

    const handleFotoChange = (e) => {
        const file = e.target.files?.[0];
        if (file && file.size > 5 * 1024 * 1024) {
            toast.warn('La imagen no puede superar 5 MB');
            return;
        }
        setFoto(file || null);
    };

    const buildContexto = () => {
        const lineas = [
            `Tipo: ${tipo}`,
            '',
            `Descripción:`,
            descripcion.trim(),
            '',
            '--- Contexto automático ---',
            `Estado del trámite: ${tramite?.nombreEstadoActual || 'N/A'}`,
            `Trámite ID: ${tramite?.idTramite || 'N/A'}`,
            `URL: ${location.pathname}`,
            `Fecha/Hora: ${new Date().toLocaleString('es-AR')}`,
            `Modo Edición activo: ${isEditMode ? 'Sí' : 'No'}`,
        ];

        const numCustomizaciones = Object.keys(customizations).length;
        if (numCustomizaciones > 0) {
            lineas.push(`Personalizaciones activas: ${numCustomizaciones} label(s) modificado(s)`);
            Object.entries(customizations).forEach(([key, val]) => {
                lineas.push(`  • ${key}: "${val}"`);
            });
        }

        return lineas.join('\n');
    };

    const handleEnviar = async () => {
        if (!descripcion.trim()) {
            toast.error('La descripción es obligatoria');
            return;
        }

        setEnviando(true);
        try {
            const titulo = `[${tipo}] Trámite #${tramite?.idTramite || '?'} — ${tramite?.nombreEstadoActual || 'estado desconocido'}`;

            await feedbackService.crearTicket({
                usuarioId: user?.id || user?.idUsuario,
                titulo,
                descripcionInicial: buildContexto(),
                nuevasEtiquetas: [tipo],
            }, foto || undefined);

            toast.success('Feedback enviado. ¡Gracias por el reporte!');
            setOpen(false);
            setTipo('Problema');
            setDescripcion('');
            setFoto(null);
        } catch (error) {
            console.error('Error al enviar feedback:', error);
            toast.error('No se pudo enviar el feedback: ' + (error.response?.data?.message || error.message));
        } finally {
            setEnviando(false);
        }
    };

    const handleCerrar = () => {
        setOpen(false);
        setTipo('Problema');
        setDescripcion('');
        setFoto(null);
    };

    const tipoActual = TIPOS.find(t => t.value === tipo);

    return (
        <>
            {/* Botón flotante */}
            {!calendarioAbierto && (
                <button
                    onClick={() => setOpen(true)}
                    title="Reportar un problema o sugerencia"
                    style={{
                        position: 'fixed',
                        bottom: 28,
                        right: 28,
                        zIndex: 1050,
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        backgroundColor: '#6f42c1',
                        color: '#fff',
                        border: 'none',
                        boxShadow: 'var(--shadow-md)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(111,66,193,0.6)';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(111,66,193,0.45)';
                    }}
                >
                    <FeedbackIcon />
                </button>
            )}

            {/* Modal de feedback */}
            {open && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1060,
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-end',
                        padding: '0 28px 90px 0',
                    }}
                    onClick={e => { if (e.target === e.currentTarget) handleCerrar(); }}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 16,
                            boxShadow: 'var(--shadow-lg)',
                            width: 380,
                            maxWidth: '95vw',
                            padding: '20px 20px 16px',
                            animation: 'slideUpFade 0.2s ease',
                        }}
                    >
                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <FeedbackIcon style={{ color: '#6f42c1' }} />
                                <strong style={{ color: '#1a1a2e', fontSize: '1rem' }}>Enviar Feedback</strong>
                            </div>
                            <button
                                onClick={handleCerrar}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6c757d' }}
                            >
                                <CloseIcon fontSize="small" />
                            </button>
                        </div>

                        {/* Tipo */}
                        <div className="mb-3">
                            <label className="form-label small fw-semibold text-muted mb-2">Tipo</label>
                            <div className="d-flex gap-2">
                                {TIPOS.map(t => (
                                    <button
                                        key={t.value}
                                        onClick={() => setTipo(t.value)}
                                        style={{
                                            flex: 1,
                                            padding: '6px 4px',
                                            borderRadius: 8,
                                            border: `2px solid ${tipo === t.value ? t.color : '#dee2e6'}`,
                                            background: tipo === t.value ? `${t.color}15` : '#fff',
                                            color: tipo === t.value ? t.color : '#6c757d',
                                            cursor: 'pointer',
                                            fontSize: '0.78rem',
                                            fontWeight: tipo === t.value ? 700 : 400,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 2,
                                            transition: 'all 0.15s',
                                        }}
                                    >
                                        {t.icon}
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Descripción */}
                        <div className="mb-3">
                            <label className="form-label small fw-semibold text-muted mb-1">
                                Descripción <span className="text-danger">*</span>
                            </label>
                            <textarea
                                className="form-control"
                                rows={4}
                                placeholder={
                                    tipo === 'Problema' ? 'Describí qué pasó y cómo reproducirlo...' :
                                    tipo === 'Sugerencia' ? 'Describí qué mejoraría o qué faltaría...' :
                                    'Escribí tu consulta...'
                                }
                                value={descripcion}
                                onChange={e => setDescripcion(e.target.value)}
                                style={{ resize: 'vertical', borderRadius: 8 }}
                            />
                        </div>

                        {/* Adjunto foto */}
                        <div className="mb-3">
                            <label className="form-label small fw-semibold text-muted mb-1">
                                Captura / imagen (opcional, máx. 5 MB)
                            </label>
                            <input
                                type="file"
                                accept="image/*"
                                className="form-control form-control-sm"
                                onChange={handleFotoChange}
                                style={{ borderRadius: 8, fontSize: '0.85rem' }}
                            />
                            {foto && (
                                <small className="text-success mt-1 d-block">
                                    📎 {foto.name} ({(foto.size / 1024).toFixed(0)} KB)
                                </small>
                            )}
                        </div>

                        {/* Contexto automático — solo informativo */}
                        <div
                            className="rounded-3 p-2 mb-3"
                            style={{ background: '#f8f9fa', border: '1px solid #e9ecef', fontSize: '0.75rem', color: '#6c757d' }}
                        >
                            <strong>Contexto capturado automáticamente:</strong>
                            <div className="mt-1">
                                Estado: <strong>{tramite?.nombreEstadoActual || 'N/A'}</strong>
                                {' · '}Modo edición: <strong>{isEditMode ? 'Activo' : 'Inactivo'}</strong>
                                {' · '}{new Date().toLocaleString('es-AR')}
                            </div>
                        </div>

                        {/* Botón enviar */}
                        <button
                            className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 rounded-3 fw-semibold"
                            onClick={handleEnviar}
                            disabled={enviando || !descripcion.trim()}
                            style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
                        >
                            {enviando ? (
                                <span className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                                <SendIcon fontSize="small" />
                            )}
                            {enviando ? 'Enviando...' : 'Enviar Feedback'}
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUpFade {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
}

export default FeedbackButton;
